/**
 * Permission System Constants
 * 
 * Defines all roles, permissions, and their relationships
 * Used throughout the app for role-based access control
 */

// ============================================================================
// ROLES
// ============================================================================

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  FINANCIAL_MANAGER: 'financial_manager',
  OPERATIONS_MANAGER: 'operations_manager',
  ACCOUNTANT: 'accountant',
  OPERATOR: 'operator',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

// ============================================================================
// PERMISSIONS
// ============================================================================

export const PERMISSIONS = {
  // Dashboard
  VIEW_DASHBOARD: 'view_dashboard',
  
  // User Management
  VIEW_USERS: 'view_users',
  CREATE_USERS: 'create_users',
  EDIT_USERS: 'edit_users',
  DELETE_USERS: 'delete_users',
  MANAGE_ROLES: 'manage_roles',
  
  // Vehicles
  VIEW_VEHICLES: 'view_vehicles',
  CREATE_VEHICLES: 'create_vehicles',
  EDIT_VEHICLES: 'edit_vehicles',
  DELETE_VEHICLES: 'delete_vehicles',
  
  // Maintenance
  VIEW_MAINTENANCE: 'view_maintenance',
  CREATE_JOB_CARDS: 'create_job_cards',
  EDIT_JOB_CARDS: 'edit_job_cards',
  DELETE_JOB_CARDS: 'delete_job_cards',
  VIEW_LIVE_MAP: 'view_live_map',
  
  // Drivers
  VIEW_DRIVERS: 'view_drivers',
  CREATE_DRIVERS: 'create_drivers',
  EDIT_DRIVERS: 'edit_drivers',
  DELETE_DRIVERS: 'delete_drivers',
  
  // Assignments
  VIEW_ASSIGNMENTS: 'view_assignments',
  CREATE_ASSIGNMENTS: 'create_assignments',
  EDIT_ASSIGNMENTS: 'edit_assignments',
  DELETE_ASSIGNMENTS: 'delete_assignments',
  
  // Forms
  VIEW_FORMS: 'view_forms',
  CREATE_DELIVERY_FORMS: 'create_delivery_forms',
  CREATE_RECEIVING_FORMS: 'create_receiving_forms',
  EDIT_FORMS: 'edit_forms',
  DELETE_FORMS: 'delete_forms',
  
  // Accounting
  VIEW_ACCOUNTING: 'view_accounting',
  CREATE_ACCOUNTING: 'create_accounting',
  EDIT_ACCOUNTING: 'edit_accounting',
  DELETE_ACCOUNTING: 'delete_accounting',
  APPROVE_TRANSACTIONS: 'approve_transactions',
  
  // Reports
  VIEW_REPORTS: 'view_reports',
  EXPORT_REPORTS: 'export_reports',
  CREATE_REPORTS: 'create_reports',
  
  // Settings
  VIEW_SETTINGS: 'view_settings',
  EDIT_SETTINGS: 'edit_settings',
  
  // HR Management
  VIEW_HR: 'view_hr',
  MANAGE_HR: 'manage_hr',
  APPROVE_LEAVE: 'approve_leave',
  MANAGE_PAYROLL: 'manage_payroll',
  
  // System
  VIEW_AUDIT_LOG: 'view_audit_log',
  SYSTEM_ADMIN: 'system_admin',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// ============================================================================
// PERMISSION LABELS (for UI display)
// ============================================================================

export const PERMISSION_LABELS: Record<Permission, string> = {
  // Dashboard
  [PERMISSIONS.VIEW_DASHBOARD]: 'View Dashboard',
  
  // User Management
  [PERMISSIONS.VIEW_USERS]: 'View Users',
  [PERMISSIONS.CREATE_USERS]: 'Create Users',
  [PERMISSIONS.EDIT_USERS]: 'Edit Users',
  [PERMISSIONS.DELETE_USERS]: 'Delete Users',
  [PERMISSIONS.MANAGE_ROLES]: 'Manage User Roles',
  
  // Vehicles
  [PERMISSIONS.VIEW_VEHICLES]: 'View Vehicles',
  [PERMISSIONS.CREATE_VEHICLES]: 'Create Vehicles',
  [PERMISSIONS.EDIT_VEHICLES]: 'Edit Vehicles',
  [PERMISSIONS.DELETE_VEHICLES]: 'Delete Vehicles',
  
  // Maintenance
  [PERMISSIONS.VIEW_MAINTENANCE]: 'View Maintenance',
  [PERMISSIONS.CREATE_JOB_CARDS]: 'Create Job Cards',
  [PERMISSIONS.EDIT_JOB_CARDS]: 'Edit Job Cards',
  [PERMISSIONS.DELETE_JOB_CARDS]: 'Delete Job Cards',
  [PERMISSIONS.VIEW_LIVE_MAP]: 'View Live Map',
  
  // Drivers
  [PERMISSIONS.VIEW_DRIVERS]: 'View Drivers',
  [PERMISSIONS.CREATE_DRIVERS]: 'Create Drivers',
  [PERMISSIONS.EDIT_DRIVERS]: 'Edit Drivers',
  [PERMISSIONS.DELETE_DRIVERS]: 'Delete Drivers',
  
  // Assignments
  [PERMISSIONS.VIEW_ASSIGNMENTS]: 'View Assignments',
  [PERMISSIONS.CREATE_ASSIGNMENTS]: 'Create Assignments',
  [PERMISSIONS.EDIT_ASSIGNMENTS]: 'Edit Assignments',
  [PERMISSIONS.DELETE_ASSIGNMENTS]: 'Delete Assignments',
  
  // Forms
  [PERMISSIONS.VIEW_FORMS]: 'View Forms',
  [PERMISSIONS.CREATE_DELIVERY_FORMS]: 'Create Delivery Forms',
  [PERMISSIONS.CREATE_RECEIVING_FORMS]: 'Create Receiving Forms',
  [PERMISSIONS.EDIT_FORMS]: 'Edit Forms',
  [PERMISSIONS.DELETE_FORMS]: 'Delete Forms',
  
  // Accounting
  [PERMISSIONS.VIEW_ACCOUNTING]: 'View Accounting',
  [PERMISSIONS.CREATE_ACCOUNTING]: 'Create Accounting Entries',
  [PERMISSIONS.EDIT_ACCOUNTING]: 'Edit Accounting',
  [PERMISSIONS.DELETE_ACCOUNTING]: 'Delete Accounting',
  [PERMISSIONS.APPROVE_TRANSACTIONS]: 'Approve Transactions',
  
  // Reports
  [PERMISSIONS.VIEW_REPORTS]: 'View Reports',
  [PERMISSIONS.EXPORT_REPORTS]: 'Export Reports',
  [PERMISSIONS.CREATE_REPORTS]: 'Create Reports',
  
  // Settings
  [PERMISSIONS.VIEW_SETTINGS]: 'View Settings',
  [PERMISSIONS.EDIT_SETTINGS]: 'Edit Settings',
  
  // HR Management
  [PERMISSIONS.VIEW_HR]: 'View HR Data',
  [PERMISSIONS.MANAGE_HR]: 'Manage HR',
  [PERMISSIONS.APPROVE_LEAVE]: 'Approve Leave Requests',
  [PERMISSIONS.MANAGE_PAYROLL]: 'Manage Payroll',
  
  // System
  [PERMISSIONS.VIEW_AUDIT_LOG]: 'View Audit Log',
  [PERMISSIONS.SYSTEM_ADMIN]: 'System Administration',
};

// ============================================================================
// PERMISSION DESCRIPTIONS (for tooltips)
// ============================================================================

export const PERMISSION_DESCRIPTIONS: Record<Permission, string> = {
  // Dashboard
  [PERMISSIONS.VIEW_DASHBOARD]: 'Access main dashboard with overview metrics',
  
  // User Management
  [PERMISSIONS.VIEW_USERS]: 'View list of all users and their details',
  [PERMISSIONS.CREATE_USERS]: 'Create new user accounts',
  [PERMISSIONS.EDIT_USERS]: 'Edit existing user information',
  [PERMISSIONS.DELETE_USERS]: 'Delete user accounts (irreversible)',
  [PERMISSIONS.MANAGE_ROLES]: 'Assign and change user roles',
  
  // Vehicles
  [PERMISSIONS.VIEW_VEHICLES]: 'View vehicle list and details',
  [PERMISSIONS.CREATE_VEHICLES]: 'Add new vehicles to the system',
  [PERMISSIONS.EDIT_VEHICLES]: 'Edit vehicle information (license plate, model, etc.)',
  [PERMISSIONS.DELETE_VEHICLES]: 'Remove vehicles from the system',
  
  // Maintenance
  [PERMISSIONS.VIEW_MAINTENANCE]: 'Access maintenance module and job cards',
  [PERMISSIONS.CREATE_JOB_CARDS]: 'Create new maintenance job cards',
  [PERMISSIONS.EDIT_JOB_CARDS]: 'Edit existing job cards',
  [PERMISSIONS.DELETE_JOB_CARDS]: 'Delete job cards',
  [PERMISSIONS.VIEW_LIVE_MAP]: 'Access live vehicle tracking map',
  
  // Drivers
  [PERMISSIONS.VIEW_DRIVERS]: 'View driver list and details',
  [PERMISSIONS.CREATE_DRIVERS]: 'Add new drivers to the system',
  [PERMISSIONS.EDIT_DRIVERS]: 'Edit driver information',
  [PERMISSIONS.DELETE_DRIVERS]: 'Remove drivers from the system',
  
  // Assignments
  [PERMISSIONS.VIEW_ASSIGNMENTS]: 'View vehicle-driver assignments',
  [PERMISSIONS.CREATE_ASSIGNMENTS]: 'Assign vehicles to drivers',
  [PERMISSIONS.EDIT_ASSIGNMENTS]: 'Modify existing assignments',
  [PERMISSIONS.DELETE_ASSIGNMENTS]: 'Remove vehicle-driver assignments',
  
  // Forms
  [PERMISSIONS.VIEW_FORMS]: 'View delivery and receiving forms',
  [PERMISSIONS.CREATE_DELIVERY_FORMS]: 'Create vehicle delivery forms',
  [PERMISSIONS.CREATE_RECEIVING_FORMS]: 'Create vehicle receiving forms',
  [PERMISSIONS.EDIT_FORMS]: 'Edit existing forms',
  [PERMISSIONS.DELETE_FORMS]: 'Delete forms',
  
  // Accounting
  [PERMISSIONS.VIEW_ACCOUNTING]: 'View accounting data (expenses, income, rentals)',
  [PERMISSIONS.CREATE_ACCOUNTING]: 'Add new accounting entries',
  [PERMISSIONS.EDIT_ACCOUNTING]: 'Modify accounting entries',
  [PERMISSIONS.DELETE_ACCOUNTING]: 'Delete accounting entries',
  [PERMISSIONS.APPROVE_TRANSACTIONS]: 'Approve high-value financial transactions',
  
  // Reports
  [PERMISSIONS.VIEW_REPORTS]: 'View all system reports',
  [PERMISSIONS.EXPORT_REPORTS]: 'Export reports to Excel/PDF',
  [PERMISSIONS.CREATE_REPORTS]: 'Create custom reports',
  
  // Settings
  [PERMISSIONS.VIEW_SETTINGS]: 'View system settings',
  [PERMISSIONS.EDIT_SETTINGS]: 'Modify system settings',
  
  // HR Management
  [PERMISSIONS.VIEW_HR]: 'View employee database, contracts, leave, attendance',
  [PERMISSIONS.MANAGE_HR]: 'Add, edit, delete employees and HR records',
  [PERMISSIONS.APPROVE_LEAVE]: 'Approve or reject employee leave requests',
  [PERMISSIONS.MANAGE_PAYROLL]: 'Manage salary payments and payroll records',
  
  // System
  [PERMISSIONS.VIEW_AUDIT_LOG]: 'View audit trail of system changes',
  [PERMISSIONS.SYSTEM_ADMIN]: 'Full system administration access',
};

// ============================================================================
// PERMISSION DEPENDENCIES
// ============================================================================

/**
 * Defines which permissions require other permissions
 * Example: To edit vehicles, you must first have view vehicles permission
 */
export const PERMISSION_DEPENDENCIES: Partial<Record<Permission, Permission[]>> = {
  // Creating requires viewing
  [PERMISSIONS.CREATE_USERS]: [PERMISSIONS.VIEW_USERS],
  [PERMISSIONS.CREATE_VEHICLES]: [PERMISSIONS.VIEW_VEHICLES],
  [PERMISSIONS.CREATE_DRIVERS]: [PERMISSIONS.VIEW_DRIVERS],
  [PERMISSIONS.CREATE_ASSIGNMENTS]: [PERMISSIONS.VIEW_ASSIGNMENTS],
  [PERMISSIONS.CREATE_ACCOUNTING]: [PERMISSIONS.VIEW_ACCOUNTING],
  [PERMISSIONS.CREATE_REPORTS]: [PERMISSIONS.VIEW_REPORTS],
  [PERMISSIONS.CREATE_JOB_CARDS]: [PERMISSIONS.VIEW_MAINTENANCE],
  
  // Editing requires viewing
  [PERMISSIONS.EDIT_USERS]: [PERMISSIONS.VIEW_USERS],
  [PERMISSIONS.EDIT_VEHICLES]: [PERMISSIONS.VIEW_VEHICLES],
  [PERMISSIONS.EDIT_DRIVERS]: [PERMISSIONS.VIEW_DRIVERS],
  [PERMISSIONS.EDIT_ASSIGNMENTS]: [PERMISSIONS.VIEW_ASSIGNMENTS],
  [PERMISSIONS.EDIT_FORMS]: [PERMISSIONS.VIEW_FORMS],
  [PERMISSIONS.EDIT_ACCOUNTING]: [PERMISSIONS.VIEW_ACCOUNTING],
  [PERMISSIONS.EDIT_SETTINGS]: [PERMISSIONS.VIEW_SETTINGS],
  [PERMISSIONS.EDIT_JOB_CARDS]: [PERMISSIONS.VIEW_MAINTENANCE],
  
  // Deleting requires both viewing and editing
  [PERMISSIONS.DELETE_USERS]: [PERMISSIONS.VIEW_USERS, PERMISSIONS.EDIT_USERS],
  [PERMISSIONS.DELETE_VEHICLES]: [PERMISSIONS.VIEW_VEHICLES, PERMISSIONS.EDIT_VEHICLES],
  [PERMISSIONS.DELETE_DRIVERS]: [PERMISSIONS.VIEW_DRIVERS, PERMISSIONS.EDIT_DRIVERS],
  [PERMISSIONS.DELETE_ASSIGNMENTS]: [PERMISSIONS.VIEW_ASSIGNMENTS, PERMISSIONS.EDIT_ASSIGNMENTS],
  [PERMISSIONS.DELETE_FORMS]: [PERMISSIONS.VIEW_FORMS, PERMISSIONS.EDIT_FORMS],
  [PERMISSIONS.DELETE_ACCOUNTING]: [PERMISSIONS.VIEW_ACCOUNTING, PERMISSIONS.EDIT_ACCOUNTING],
  [PERMISSIONS.DELETE_JOB_CARDS]: [PERMISSIONS.VIEW_MAINTENANCE, PERMISSIONS.EDIT_JOB_CARDS],
  
  // Managing roles requires user management
  [PERMISSIONS.MANAGE_ROLES]: [PERMISSIONS.VIEW_USERS, PERMISSIONS.EDIT_USERS],
  
  // Exporting requires viewing
  [PERMISSIONS.EXPORT_REPORTS]: [PERMISSIONS.VIEW_REPORTS],
  
  // Forms require viewing vehicles and drivers
  [PERMISSIONS.CREATE_DELIVERY_FORMS]: [PERMISSIONS.VIEW_VEHICLES, PERMISSIONS.VIEW_DRIVERS],
  [PERMISSIONS.CREATE_RECEIVING_FORMS]: [PERMISSIONS.VIEW_VEHICLES, PERMISSIONS.VIEW_DRIVERS],
  
  // Approving transactions requires viewing accounting
  [PERMISSIONS.APPROVE_TRANSACTIONS]: [PERMISSIONS.VIEW_ACCOUNTING],
};

// ============================================================================
// ROLE PERMISSIONS (Default permissions for each role)
// ============================================================================

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  /**
   * SUPER ADMIN - Full system access
   */
  [ROLES.SUPER_ADMIN]: Object.values(PERMISSIONS),
  
  /**
   * FINANCIAL MANAGER - Full accounting access, read-only operations
   */
  [ROLES.FINANCIAL_MANAGER]: [
    // Dashboard
    PERMISSIONS.VIEW_DASHBOARD,
    
    // User Management - Read only
    PERMISSIONS.VIEW_USERS,
    
    // Vehicles - Read only (for context)
    PERMISSIONS.VIEW_VEHICLES,
    
    // Maintenance - Read only
    PERMISSIONS.VIEW_MAINTENANCE,
    PERMISSIONS.VIEW_LIVE_MAP,
    
    // Drivers - Read only (for context)
    PERMISSIONS.VIEW_DRIVERS,
    
    // Assignments - Read only (for context)
    PERMISSIONS.VIEW_ASSIGNMENTS,
    
    // Forms - Read only
    PERMISSIONS.VIEW_FORMS,
    
    // Accounting - Full access
    PERMISSIONS.VIEW_ACCOUNTING,
    PERMISSIONS.CREATE_ACCOUNTING,
    PERMISSIONS.EDIT_ACCOUNTING,
    PERMISSIONS.DELETE_ACCOUNTING,
    PERMISSIONS.APPROVE_TRANSACTIONS,
    
    // Reports - Full access
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.CREATE_REPORTS,
  ],
  
  /**
   * OPERATIONS MANAGER - Full operations access with view-only accounting
   */
  [ROLES.OPERATIONS_MANAGER]: [
    // Dashboard
    PERMISSIONS.VIEW_DASHBOARD,
    
    // Vehicles - Full access
    PERMISSIONS.VIEW_VEHICLES,
    PERMISSIONS.CREATE_VEHICLES,
    PERMISSIONS.EDIT_VEHICLES,
    PERMISSIONS.DELETE_VEHICLES,
    
    // Maintenance - Full access
    PERMISSIONS.VIEW_MAINTENANCE,
    PERMISSIONS.CREATE_JOB_CARDS,
    PERMISSIONS.EDIT_JOB_CARDS,
    PERMISSIONS.DELETE_JOB_CARDS,
    PERMISSIONS.VIEW_LIVE_MAP,
    
    // Drivers - Full access
    PERMISSIONS.VIEW_DRIVERS,
    PERMISSIONS.CREATE_DRIVERS,
    PERMISSIONS.EDIT_DRIVERS,
    PERMISSIONS.DELETE_DRIVERS,
    
    // Assignments - Full access
    PERMISSIONS.VIEW_ASSIGNMENTS,
    PERMISSIONS.CREATE_ASSIGNMENTS,
    PERMISSIONS.EDIT_ASSIGNMENTS,
    PERMISSIONS.DELETE_ASSIGNMENTS,
    
    // Forms - For creating delivery/receiving forms
    PERMISSIONS.VIEW_FORMS,
    PERMISSIONS.CREATE_DELIVERY_FORMS,
    PERMISSIONS.CREATE_RECEIVING_FORMS,
    
    // Accounting - View only
    PERMISSIONS.VIEW_ACCOUNTING,
    
    // Reports - Full access
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
    PERMISSIONS.CREATE_REPORTS,
    
    // Settings - View only
    PERMISSIONS.VIEW_SETTINGS,
  ],
  
  /**
   * ACCOUNTANT - Accounting and financial data only, no vehicle operations
   */
  [ROLES.ACCOUNTANT]: [
    // Dashboard
    PERMISSIONS.VIEW_DASHBOARD,
    
    // NO access to Vehicles, Drivers, Assignments, or Forms
    // Accountants work only with financial data
    
    // Accounting - Full access (Create/Edit/View, no delete or approve)
    PERMISSIONS.VIEW_ACCOUNTING,
    PERMISSIONS.CREATE_ACCOUNTING,
    PERMISSIONS.EDIT_ACCOUNTING,
    
    // Reports - View only
    PERMISSIONS.VIEW_REPORTS,
    PERMISSIONS.EXPORT_REPORTS,
    
    // Settings - View only (to see their own profile)
    PERMISSIONS.VIEW_SETTINGS,
  ],
  
  /**
   * OPERATOR - Assignment management ONLY (no dashboard, no other screens)
   */
  [ROLES.OPERATOR]: [
    // Assignments - Full access (this is their ONLY screen)
    PERMISSIONS.VIEW_ASSIGNMENTS,
    PERMISSIONS.EDIT_ASSIGNMENTS,
    PERMISSIONS.CREATE_ASSIGNMENTS,
  ],
};

// ============================================================================
// PERMISSION GROUPS (for UI organization)
// ============================================================================

export interface PermissionGroup {
  name: string;
  icon: string;
  permissions: Permission[];
  description: string;
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    name: 'Dashboard',
    icon: '📊',
    description: 'Main dashboard access',
    permissions: [
      PERMISSIONS.VIEW_DASHBOARD,
    ],
  },
  {
    name: 'User Management',
    icon: '👥',
    description: 'Manage system users and their access',
    permissions: [
      PERMISSIONS.VIEW_USERS,
      PERMISSIONS.CREATE_USERS,
      PERMISSIONS.EDIT_USERS,
      PERMISSIONS.DELETE_USERS,
      PERMISSIONS.MANAGE_ROLES,
    ],
  },
  {
    name: 'Vehicles',
    icon: '🚗',
    description: 'Manage fleet vehicles',
    permissions: [
      PERMISSIONS.VIEW_VEHICLES,
      PERMISSIONS.CREATE_VEHICLES,
      PERMISSIONS.EDIT_VEHICLES,
      PERMISSIONS.DELETE_VEHICLES,
    ],
  },
  {
    name: 'Maintenance',
    icon: '🔧',
    description: 'Vehicle maintenance and job cards',
    permissions: [
      PERMISSIONS.VIEW_MAINTENANCE,
      PERMISSIONS.CREATE_JOB_CARDS,
      PERMISSIONS.EDIT_JOB_CARDS,
      PERMISSIONS.DELETE_JOB_CARDS,
    ],
  },
  {
    name: 'Live Map',
    icon: '🗺️',
    description: 'Real-time vehicle tracking',
    permissions: [
      PERMISSIONS.VIEW_LIVE_MAP,
    ],
  },
  {
    name: 'Drivers',
    icon: '👤',
    description: 'Manage drivers',
    permissions: [
      PERMISSIONS.VIEW_DRIVERS,
      PERMISSIONS.CREATE_DRIVERS,
      PERMISSIONS.EDIT_DRIVERS,
      PERMISSIONS.DELETE_DRIVERS,
    ],
  },
  {
    name: 'Assignments',
    icon: '📋',
    description: 'Manage vehicle-driver assignments',
    permissions: [
      PERMISSIONS.VIEW_ASSIGNMENTS,
      PERMISSIONS.CREATE_ASSIGNMENTS,
      PERMISSIONS.EDIT_ASSIGNMENTS,
      PERMISSIONS.DELETE_ASSIGNMENTS,
    ],
  },
  {
    name: 'Forms',
    icon: '📝',
    description: 'Delivery and receiving forms',
    permissions: [
      PERMISSIONS.VIEW_FORMS,
      PERMISSIONS.CREATE_DELIVERY_FORMS,
      PERMISSIONS.CREATE_RECEIVING_FORMS,
      PERMISSIONS.EDIT_FORMS,
      PERMISSIONS.DELETE_FORMS,
    ],
  },
  {
    name: 'Accounting',
    icon: '💰',
    description: 'Financial data and transactions',
    permissions: [
      PERMISSIONS.VIEW_ACCOUNTING,
      PERMISSIONS.CREATE_ACCOUNTING,
      PERMISSIONS.EDIT_ACCOUNTING,
      PERMISSIONS.DELETE_ACCOUNTING,
      PERMISSIONS.APPROVE_TRANSACTIONS,
    ],
  },
  {
    name: 'Reports',
    icon: '📊',
    description: 'View and export reports',
    permissions: [
      PERMISSIONS.VIEW_REPORTS,
      PERMISSIONS.EXPORT_REPORTS,
      PERMISSIONS.CREATE_REPORTS,
    ],
  },
  {
    name: 'HR Management',
    icon: '👥',
    description: 'Employee and HR management',
    permissions: [
      PERMISSIONS.VIEW_HR,
      PERMISSIONS.MANAGE_HR,
      PERMISSIONS.APPROVE_LEAVE,
      PERMISSIONS.MANAGE_PAYROLL,
    ],
  },
  {
    name: 'Settings',
    icon: '⚙️',
    description: 'System settings and configuration',
    permissions: [
      PERMISSIONS.VIEW_SETTINGS,
      PERMISSIONS.EDIT_SETTINGS,
    ],
  },
  {
    name: 'System',
    icon: '🛠️',
    description: 'System administration',
    permissions: [
      PERMISSIONS.VIEW_AUDIT_LOG,
      PERMISSIONS.SYSTEM_ADMIN,
    ],
  },
];

// ============================================================================
// ROLE LABELS (for UI display)
// ============================================================================

export const ROLE_LABELS: Record<Role, string> = {
  [ROLES.SUPER_ADMIN]: 'Super Admin',
  [ROLES.FINANCIAL_MANAGER]: 'Financial Manager',
  [ROLES.OPERATIONS_MANAGER]: 'Operations Manager',
  [ROLES.ACCOUNTANT]: 'Accountant',
  [ROLES.OPERATOR]: 'Operator',
};

// ============================================================================
// ROLE DESCRIPTIONS (for UI tooltips)
// ============================================================================

export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  [ROLES.SUPER_ADMIN]: 'Full system access. Can manage users, settings, and all data.',
  [ROLES.FINANCIAL_MANAGER]: 'Full accounting access with read-only operations access.',
  [ROLES.OPERATIONS_MANAGER]: 'Full operations access with read-only accounting access.',
  [ROLES.ACCOUNTANT]: 'Can enter accounting data and view operational context.',
  [ROLES.OPERATOR]: 'Basic operations - can create forms and view vehicles/drivers.',
};
